import React, { useEffect, useState, useRef } from "react";
import {
  Card, CardBody, Row, Col, Input, Button, Table, Badge,
  Modal, ModalHeader, ModalBody
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { formatIST, getCookie } from "../../Helpers/Utils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";


const QuotationList = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(s => s.loading.loading);
  const user = useSelector(s => s.customer.customer);

  /* ================= LIST ================= */
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const searchTimer = useRef(null);

  /* ================= VIEW MODAL ================= */
  const [open, setOpen] = useState(false);
  const [quotation, setQuotation] = useState(null);

  const loadView = async (id) => {
    dispatch(loadingTrue());

    const res = await Handler({
      method: "POST",
      url: "/quotation/view.php",
      data: { quotation_id: id, outlet_id: user?.outlet_id }
    });

    dispatch(loadingFalse());

    if (!res?.success) return toast.error(res?.msg);

    setQuotation(res.data);
    setOpen(true);
  };

  /* ================= LOAD LIST ================= */
  const loadData = async () => {
    dispatch(loadingTrue());

    const res = await Handler({
      method: "POST",
      url: "/quotation/list.php",
      data: {
        outlet_id: user?.outlet_id,
        quotation_no: search,
        date_from: startDate,
        date_to: endDate,
        sort_order: sortOrder,
        page,
        limit
      }
    });

    dispatch(loadingFalse());

    if (!res?.success) return toast.error(res?.msg);

    setRows(res.data.data || []);
    setTotal(res.data.total_records || 0);
  };

  /* 🔍 SEARCH */
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      setSearch(searchText);
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchText]);

  useEffect(() => { setPage(1); }, [search, startDate, endDate, sortOrder]);
  useEffect(() => { loadData(); }, [page, search, startDate, endDate, sortOrder]);

  const statusColor = s => {
    if (s === "approved") return "success";
    if (s === "pending") return "warning";
    if (s === "rejected") return "danger";
    return "secondary";
  };

  const downloadPDF = async (id) => {
    dispatch(loadingTrue());

    const token = getCookie("token");

    if (!token) {
      toast.error("Login expired");
      dispatch(loadingFalse());
      return;
    }

    // ✅ OPEN TAB FIRST (avoid popup block)
    const newTab = window.open("", "_blank");

    if (!newTab) {
      toast.error("Popup blocked! Please allow popups.");
      dispatch(loadingFalse());
      return;
    }

    newTab.document.write("<h3 style='font-family:sans-serif'>Loading Quotation...</h3>");

    try {
      const res = await axios.post(
        "https://billing.qads.co.in/qads-billing/apis/quotation/generate.php",
        {
          quotation_id: id,
          outlet_id: user?.outlet_id,
        },
        {
          responseType: "blob",
          headers: {
            Authorization: "Bearer " + token,
          },
        }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // ✅ LOAD PDF INTO ALREADY OPENED TAB
      newTab.location.href = url;

      // ✅ OPTIONAL AUTO PRINT (may work / may not)
      newTab.onload = () => {
        try {
          newTab.focus();
          newTab.print();
        } catch (e) {
          console.log("Auto print blocked");
        }
      };

      // ✅ CLEANUP (optional delay)
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);

    } catch (err) {
      console.error("Print Error:", err);
      newTab.close(); // close empty tab on error
      toast.error("Failed to open invoice");
    }

    dispatch(loadingFalse());
  };


  return (
    <>
      <Row className="mb-3">
        <Col><h5 className="fw-bold">Quotations</h5></Col>
      </Row>

      <Card className="shadow-sm">
        <CardBody>

          {/* FILTERS */}
          <Row className="mb-3 g-2 align-items-end">
            <Col md={3}>
              <Input placeholder="Quotation No"
                value={searchText}
                onChange={e => setSearchText(e.target.value)} />
            </Col>

            <Col md={2}>
              <Input type="date" value={startDate}
                onChange={e => setStartDate(e.target.value)} />
            </Col>

            <Col md={2}>
              <Input type="date" value={endDate}
                onChange={e => setEndDate(e.target.value)} />
            </Col>

            <Col md={2}>
              <Input type="select" value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}>
                <option value="DESC">Newest First</option>
                <option value="ASC">Oldest First</option>
              </Input>
            </Col>

            <Col md={3} className="text-end">
              <Button color="dark" onClick={() => navigate("/quotation-create")}>
                + New Quotation
              </Button>
            </Col>
          </Row>

          {/* TABLE */}
          <Table hover responsive className="align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Quotation No</th>
                <th>Customer</th>
                <th>Date</th>
                {/* <th>Status</th> */}
                <th className="text-end">Amount</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="7" className="text-center text-muted">No records</td></tr>
              )}

              {rows?.map((r, i) => (
                <tr key={r.id}>
                  <td>{(page - 1) * limit + i + 1}</td>
                  <td className="fw-bold">{r.quotation_no}</td>
                  <td>{r.customer_name || "Walk-in Customer"}</td>
                  <td>{formatIST(r.created_at)}</td>
                  {/* <td><Badge color={statusColor(r.status)}>{r.status}</Badge></td> */}
                  <td className="text-end fw-bold">₹ {Number(r.total_amount).toFixed(2)}</td>
                  <td className="text-center">
                    <Button disabled={loading} size="sm" color="dark"
                      onClick={() => loadView(r.id)}>
                      <i className="bx bx-show"></i>
                    </Button>

                    <Button disabled={loading} size="sm" color="success" className="ms-2"
                      onClick={() => downloadPDF(r.id)}>
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            total={total}
            page={page}
            limit={limit}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => p + 1)}
          />

        </CardBody>
      </Card>

      {/* ================= VIEW MODAL ================= */}
      <Modal size="lg" isOpen={open} toggle={() => setOpen(false)}>
        <ModalHeader toggle={() => setOpen(false)}>
          Quotation #{quotation?.quotation?.quotation_no}
        </ModalHeader>
        <ModalBody>

          {quotation && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="fw-bold mb-1">Customer</h6>
                  <div className="text-capitalize">{quotation.customer?.name || "Walk-in"}</div>
                  <div>{quotation.customer?.phone}</div>
                </Col>

                <Col md={6} className="text-md-end">
                  <div><b>Date:</b> {formatIST(quotation.quotation.date)}</div>
                  {/* <div><b>Status:</b> {quotation.quotation.status}</div> */}
                </Col>
              </Row>

              <Table bordered>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>GST%</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {quotation && quotation?.items?.map((it, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{it.product_name}</td>
                      <td>{it.variant_name || "-"}</td>
                      <td>{it.quantity}</td>
                      <td>{it.gst_rate}%</td>
                      <td className="text-end">₹ {it.rate}</td>
                      <td className="text-end">₹ {it.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h5 className="text-end fw-bold mt-3">
                Total: ₹ {quotation.quotation.total_amount}
              </h5>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default QuotationList;
